import config from "@Config/index";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { AppThunk } from "@Store/index";
import { detectIframeContext, storeIframeContext } from "@Utils/iframeContext";

export interface IframeState {
  isIframe: boolean;
  parentOrigin: string | null;
  referer: string | null;
  canAccessParent: boolean;
  isCrossOrigin: boolean;
  isDetected: boolean;
  partnerDomain: string | null;
  partnerId: string | null;
  iframeSource: string | null;
}

export const initialState: IframeState = {
  isIframe: false,
  parentOrigin: null,
  referer: null,
  canAccessParent: false,
  isCrossOrigin: false,
  isDetected: false,
  partnerDomain: null,
  partnerId: null,
  iframeSource: null,
};

const iframeData = createSlice({
  name: "iframeData",
  initialState,
  reducers: {
    setIframeContext: (state, { payload }: PayloadAction<Partial<IframeState>>) => ({
      ...state,
      ...payload,
      isDetected: true,
    }),
    updatePartnerInfo: (
      state,
      { payload }: PayloadAction<{ partnerDomain?: string; partnerId?: string }>,
    ) => {
      if (payload.partnerDomain) state.partnerDomain = payload.partnerDomain;
      if (payload.partnerId) state.partnerId = payload.partnerId;
    },
    resetIframeContext: () => initialState,
  },
});

export default iframeData.reducer;

export const { setIframeContext, updatePartnerInfo, resetIframeContext } = iframeData.actions;

/**
 * Server-side middleware to detect iframe context from request
 */
export const iframeDetectionMiddlewareServer =
  ({ req }: any): AppThunk =>
  async (dispatch) => {
    // Check headers for iframe indicators
    const secFetchDest = req.headers["sec-fetch-dest"];
    const secFetchSite = req.headers["sec-fetch-site"];
    const referer = req.headers.referer || req.headers.referrer;

    // Detection methods:
    // 1. Sec-Fetch-Dest header (Chrome, Edge, Firefox) - most reliable
    const isIframeFromHeader = secFetchDest === "iframe";

    // 2. Cross-origin request with external referer (Safari fallback)
    const isCrossOrigin = secFetchSite === "cross-site" || secFetchSite === "same-site";
    const hasExternalReferer = referer && !referer.includes(req.hostname);

    // Combine detection methods
    const isIframe = isIframeFromHeader || (isCrossOrigin && hasExternalReferer);

    let partnerDomain = null;
    let partnerId = null;

    if (isIframe && referer) {
      try {
        const refererUrl = new URL(referer);
        partnerDomain = refererUrl.hostname;
      } catch (e) {
        // Invalid URL
      }
    }

    // Check for partner ID in query params
    if (req.query?.partner_id) {
      partnerId = req.query.partner_id;
    }

    // Check for iframe source attribution in query params.
    // Partners embed the widget with an `embed_source` param so leads created
    // inside the iframe can be attributed back to the embedding site.
    const iframeSource = req.query?.embed_source || null;

    const iframeContext: Partial<IframeState> = {
      isIframe,
      referer: referer || null,
      parentOrigin: null, // Can't determine on server
      canAccessParent: false, // Can't determine on server
      isCrossOrigin: isIframe && isCrossOrigin,
      partnerDomain,
      partnerId,
      iframeSource,
    };

    dispatch(setIframeContext(iframeContext));
  };

/**
 * Client-side detection of iframe context
 */
export const detectIframeContextClient = (): AppThunk => async (dispatch) => {
  if (typeof window === "undefined" || !config.IS_WHITELABEL_APP) return;

  // Detect iframe context
  const context = detectIframeContext();

  // Extract partner domain from referer
  let partnerDomain = null;
  if (context.referer) {
    try {
      const refererUrl = new URL(context.referer);
      partnerDomain = refererUrl.hostname;
    } catch (e) {
      // Invalid URL
    }
  }

  // Check for partner ID in URL
  let partnerId = null;
  const urlParams = new URLSearchParams(window.location.search);
  // chose what query param the partner id should be passed in
  if (urlParams.get("partner_id")) {
    partnerId = urlParams.get("partner_id");
  }

  const iframeContext: Partial<IframeState> = {
    ...context,
    partnerDomain,
    partnerId,
  };

  // Store in sessionStorage for persistence
  storeIframeContext(context);

  dispatch(setIframeContext(iframeContext));
};

/**
 * Update partner information
 */
export const updatePartnerInfoAction =
  (partnerDomain?: string, partnerId?: string): AppThunk =>
  async (dispatch) => {
    dispatch(updatePartnerInfo({ partnerDomain, partnerId }));
  };
